import { scrollToElement } from '@opten/scroll/scroll-to-element';
import { dispatchCustomEvent } from '@opten/events/dispatch-custom-event';
import { hasClass } from '@opten/dom/has-class';
import { addClass } from '@opten/dom/add-class';
import { toggleClass } from '@opten/dom/toggle-class';
import { removeClass } from '@opten/dom/remove-class';
import { debounce } from '@opten/reactive/debounce';
import { fitBoundsWithPadding } from '@opten/map-helpers/fit-bounds-with-padding';
import { linearTween } from '@opten/animation/linear-tween';
import { isOverflown } from '@opten/dom/is-overflown';

export interface MapSearcherOptions {
	locationClass?: string;
	mapClass?: string;
	accordeonClass?: string;
	scrollToMapAnchorClass?: string;
	typeInputClass?: string;
	filterInputClass?: string;
	locationInputClass?: string;
	locationContainerClass?: string;
	markerIcon?: {
		url: string;
		size: { width: number; height: number };
		anchor: { x: number; y: number };
	};
	clusterStyle?: any;
	mapCenter?: {
		lat: number;
		lng: number;
	};
	mapInitialZoom?: 8;
	maxZoom?: number;
	mapStyles?: any;
	radiusBounds?: number;
}

declare class OverlappingMarkerSpiderfier {
	constructor(map: any, options: any);
	public addMarker(marker: any);
}

declare const Cookiebot: any;

interface MapsLocation {
	element: Element;
	marker: google.maps.Marker;
	lat: number;
	lng: number;
	name: string;
	tags: string[];
	search: string[];
	visible: boolean;
}

export class MapSearcher {

	public static defaults: MapSearcherOptions = {
		locationClass: 'js-map-searcher-location',
		mapClass: 'js-map-searcher-map',
		accordeonClass: 'js-map-searcher-accordeon',
		scrollToMapAnchorClass: 'js-map-searcher-scroll-to-map',
		typeInputClass: 'js-map-searcher-type',
		filterInputClass: 'js-map-searcher-filter',
		locationInputClass: 'js-map-searcher-place',
		locationContainerClass: 'js-map-searcher-location-wrapper',
		markerIcon: {
			url: '/images/map-marker.png',
			size: { width: 45, height: 54 },
			anchor: { x: 22.5, y: 54 },
		},
		clusterStyle: [
			{
				url: '/images/map-cluster.png',
				height: 45,
				width: 45,
				textColor: 'white',
			}, {
				url: '/images/map-cluster2.png',
				height: 45,
				width: 45,
				textColor: 'white',
			},
			/*{
				url: '/images/map-cluster-blue.png',
				height: 45,
				width: 45,
				textColor: 'white',
			}, {
				url: '/images/map-cluster-blue2.png',
				height: 45,
				width: 45,
				textColor: 'white',
			},*/
		],
		mapCenter: { lat: 46.905358, lng: 8.423689 },
		mapInitialZoom: 8,
		maxZoom: 15,
		mapStyles: [],
		radiusBounds: 7,
	};

	private typeSearchField: HTMLInputElement[];
	private filterSearchField: HTMLInputElement[];
	private locationSearchField: HTMLInputElement;
	private accordeon: HTMLElement;
	private mapContainer: HTMLElement;
	private locationContainer: HTMLElement;
	private options: MapSearcherOptions;
	private map: google.maps.Map;
	private oms: OverlappingMarkerSpiderfier;
	private markerCluster: MarkerClusterer;
	private geoCoder: google.maps.Geocoder;
	private locations: MapsLocation[];
	private previousAddress: string;
	private previousBounds: google.maps.LatLngBounds;

	constructor(private element: HTMLElement, options?: MapSearcherOptions) {
		window.addEventListener(
			'CookiebotOnAccept',
			(e) => {
				if (Cookiebot.consent.marketing) {
					this.shopfinderMapInit();
					console.log("logged?");					
				}
			},
			false
		);
		if (Cookiebot.consent.marketing) {
			this.shopfinderMapInit();
			console.log("logged 2?");
		}
	}
	
	private shopfinderMapInit() {
		this.options = { ...MapSearcher.defaults, ...this.options };

		this.mapContainer = this.element.querySelector(
			`.${this.options.mapClass}`,
		);
		this.typeSearchField = Array.from(this.element.querySelectorAll(
			`.${this.options.typeInputClass}`,
		));
		this.filterSearchField = Array.from(this.element.querySelectorAll(
			`.${this.options.filterInputClass}`,
		));
		this.locationSearchField = this.element.querySelector(
			`.${this.options.locationInputClass}`,
		);
		this.locationContainer = this.element.querySelector(
			`.${this.options.locationContainerClass}`,
		);
		this.accordeon = this.element.querySelector(
			`.${this.options.accordeonClass}`,
		);

		this.map = new google.maps.Map(this.mapContainer, {
			zoom: this.options.mapInitialZoom,
			center: this.options.mapCenter,
			fullscreenControl: false,
			mapTypeControl: false,
			streetViewControl: false,
			styles: this.options.mapStyles,
		});
		google.maps.event.addListenerOnce(this.map, 'idle', () =>
			this.centerMap(),
		);

		this.oms = new OverlappingMarkerSpiderfier(this.map, {
			markersWontMove: true,
			markersWontHide: true,
			basicFormatEvents: true,
			circleFootSeparation: 50,
		});

		this.markerCluster = new MarkerClusterer(this.map, [], {
			maxZoom: this.options.maxZoom,
			styles: this.options.clusterStyle,
		});
		this.markerCluster.setCalculator((markers, numStyles) => {
			/*const differentIcons = markers.map((o) => o.getIcon().url.replace('-active.png', '.png')).reduce((total, amount) => {
				if (total.indexOf(amount) === -1) {
					total.push(amount);
				}
				return total;
			}, []);
			if (markers.length >= 10) {
				return { text: markers.length, index: differentIcons.length > 1 ? 4 : 2 };
			} else {
				return { text: markers.length, index: differentIcons.length > 1 ? 3 : 1 };
			}*/
			if (markers.length >= 10) {
				return { text: markers.length, index: 2 };
			} else {
				return { text: markers.length, index: 1 };
			}
		});

		this.geoCoder = new google.maps.Geocoder();

		this.locations = Array.from(
			this.element.getElementsByClassName(this.options.locationClass),
		).map((elem) => this.createMapLocation(elem));

		this.locations.forEach((l) => {
			l.marker.setMap(this.map);
			this.oms.addMarker(l.marker);
			this.markerCluster.addMarker(l.marker);

			l.marker.addListener('spider_click', () => {
				this.handleMarkerClick(l);
			});
		});

		[
			...this.typeSearchField,
			...this.filterSearchField,
		].forEach((input: HTMLInputElement) => {
			['change'].forEach((key) =>
				input.addEventListener(
					key,
					debounce(() => {
						this.search();
					}, 750, false),
				),
			);
			if (this.isInputActiveByHash(input)) {
				input.checked = true;
			}
		});
		['keyup'].forEach((key) => this.locationSearchField.addEventListener(
			key,
			debounce(() => {
				this.search();
			}, 750, false)
		));

		Array.from(
			document.getElementsByClassName(this.options.scrollToMapAnchorClass),
		).forEach((item: HTMLElement) =>
			item.addEventListener('click', () =>
				scrollToElement(this.mapContainer, linearTween, 300, -70),
			),
		);

		this.search();
	}

	private createMapLocation(element: Element) {
		const lat = parseFloat(element.getAttribute('data-lat'));
		const lng = parseFloat(element.getAttribute('data-lng'));
		const name = element.getAttribute('data-name');
		const tagsAttr = element.getAttribute('data-tags');
		const searchAttr = element.getAttribute('data-search');
		let icon = element.getAttribute('data-icon');
		let iconActive = element.getAttribute('data-icon-active');
		icon = icon ? icon : this.options.markerIcon.url;
		iconActive = iconActive ? iconActive : icon;
		let tags = [];
		let search = [];
		const marker = new google.maps.Marker({
			position: { lat, lng },
			icon: (({ url, size, anchor }) => ({
				url: icon,
				size: new google.maps.Size(size.width, size.height),
				anchor: new google.maps.Point(anchor.x, anchor.y),
			}))(this.options.markerIcon),
		});
		if (tagsAttr) {
			tags = tagsAttr.split(',');
		}
		if (searchAttr) {
			search = searchAttr.split(',');
		}
		
		const collapse = document.querySelector(element.getAttribute('data-target'));
		collapse.addEventListener('show.opten.collapse', () => {
			marker.setIcon({
				url: iconActive,
				size: new google.maps.Size(this.options.markerIcon.size.width, this.options.markerIcon.size.height),
				anchor: new google.maps.Point(this.options.markerIcon.anchor.x, this.options.markerIcon.anchor.y),
			});
		});
		collapse.addEventListener('hide.opten.collapse', () => {
			marker.setIcon({
				url: icon,
				size: new google.maps.Size(this.options.markerIcon.size.width, this.options.markerIcon.size.height),
				anchor: new google.maps.Point(this.options.markerIcon.anchor.x, this.options.markerIcon.anchor.y),
			});
		});

		return {
			element,
			lat,
			lng,
			name,
			tags,
			search,
			marker
		} as MapsLocation;
	}

	private isInputActiveByHash(input): boolean {
		if (location.hash && (input.getAttribute('type') == 'radio' || input.getAttribute('type') == 'checkbox')) {
			const val = input.getAttribute('value');
			if (val && location.hash.toLowerCase().indexOf(val.toLowerCase()) != -1) {
				if (hasClass(this.accordeon, 'is-active') == false) {
					dispatchCustomEvent(this.accordeon, 'click');
				}
				return true;
			}
		}
		return false;
	}

	private setUrlHash(hash) {
		if (location.hash != hash) {
			if (history.pushState) {
				history.pushState(null, null, hash);
			} else {
				location.hash = hash;
			}
		}
	}

	private search() {
		this.getSearchBounds().then((bounds) => {
			const anyFound = this.filterLocationsByBounds(bounds);
			if (anyFound == false) {
				this.getGeocodeSearchBounds().then((bounds) => {
					this.filterLocationsByBounds(bounds);
					this.centerMap(bounds);
				});
			} else {
				this.centerMap(bounds);
			}
		});
	}

	private filterLocationsByBounds(bounds: google.maps.LatLngBounds) {
		this.markerCluster.clearMarkers();
		const searchAddress = this.getSearchAddress();
		const searchTypes = this.typeSearchField.filter((o) => o.checked).map((o) => o.value).filter((o) => o != '');
		const searchFilters = this.filterSearchField.filter((o) => o.checked).map((o) => o.value).filter((o) => o != '');
		let anyFound = false;
		this.setUrlHash('#' + searchTypes.join('/'));
		this.locations.forEach((l) => {
			if (
				(
					!searchAddress ||
					(
						searchAddress &&
						(
							(
								bounds && bounds.contains(
									new google.maps.LatLng(l.lat, l.lng),
								)
							) ||
							l.search.some((o) => o.indexOf(searchAddress) >= 0) ||
							l.name.toLowerCase() == searchAddress
						)
					)
				) &&
				(
					(searchTypes.length == 0 || l.tags.some((o) => searchTypes.indexOf(o) >= 0)) &&
					(searchFilters.length == 0 || searchFilters.every((o) => l.tags.indexOf(o) >= 0))
				)
			) {
				this.showLocation(l);
				anyFound = true;
			} else {
				this.hideLocation(l);
			}
		});

		return anyFound;
	}

	private showLocation(l: MapsLocation) {
		l.visible = true;
		l.marker.setMap(this.map);
		this.markerCluster.addMarker(l.marker);
	}

	private hideLocation(l: MapsLocation) {
		l.visible = false;
		l.marker.setMap(null);
		this.markerCluster.removeMarker(l.marker);
	}

	private handleMarkerClick(location: MapsLocation) {
		dispatchCustomEvent(location.element, 'click');
		if (hasClass(this.accordeon, 'is-active')) {
			dispatchCustomEvent(this.accordeon, 'click');
		}
	}

	private centerMap(bounds) {
		if (this.locations && this.locations.length > 0) {
			if (!bounds) {
				bounds = this.accumulateLocationBounds(this.locations);
			}
			const top = 20;
			let left = this.locationContainer.offsetWidth * 0.3;
			if (hasClass(this.accordeon, 'is-active')) {
				left = this.locationContainer.offsetWidth;
			}
			if (window.getComputedStyle(this.locationContainer).position != 'absolute') {
				left = 20;
			}

			fitBoundsWithPadding(this.map, bounds, { top, left });
			this.setZoom();
		}
	}

	private setZoom() {
		if (this.map.getZoom() > this.options.maxZoom) {
			this.map.setZoom(this.options.maxZoom);
		}
	}

	private accumulateLocationBounds(locations: MapsLocation[]) {
		let visibles = locations.filter((o) => o.visible);
		if (!visibles || visibles.length === 0) {
			visibles = locations;
		}
		return visibles.reduce(
			(bounds, l) => bounds.extend(new google.maps.LatLng(l.lat, l.lng)),
			new google.maps.LatLngBounds()
		);
	}

	private getSearchBounds() {
		return new Promise<google.maps.LatLngBounds>((resolve) => {
			resolve(null);
		});
	}

	private getGeocodeSearchBounds() {
		return new Promise<google.maps.LatLngBounds>((resolve) => {
			const address = this.getSearchAddress();
			if (address) {
				if (address === this.previousAddress) {
					resolve(
						this.addRadiusToBounds(
							this.options.radiusBounds,
							this.previousBounds,
						),
					);
				} else {
					this.geoCoder.geocode({ address, region: "CH" }, (results, status) => {
						if (status === google.maps.GeocoderStatus.OK) {
							const bounds = results[0].geometry.bounds;
							this.previousAddress = address;
							this.previousBounds = bounds;
							resolve(
								this.addRadiusToBounds(
									this.options.radiusBounds,
									bounds,
								),
							);
						} else {
							this.previousAddress = undefined;
							this.previousBounds = undefined;
							resolve(null);
						}
					});
				}
			} else {
				resolve(null);
			}
		});
	}

	private getSearchAddress() {
		const address = this.locationSearchField.value;
		if (address) {
			return address.trim().toLowerCase();
		}
		return '';
	}

	private addRadiusToBounds(
		radius: number,
		bounds: google.maps.LatLngBounds,
	) {
		if (!bounds) {
			return null;
		}
		const ne = google.maps.geometry.spherical.computeOffset(
			bounds.getNorthEast(),
			radius * 1000,
			45,
		);
		const sw = google.maps.geometry.spherical.computeOffset(
			bounds.getSouthWest(),
			radius * 1000,
			225,
		);
		return new google.maps.LatLngBounds(sw, ne);
	}
}
